import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "@/auth/AuthContext";

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const inputCls = "bg-[#0a0a0c] border border-zinc-700 rounded px-2 py-1.5 text-xs text-white font-mono focus:border-[#d4af37] outline-none";

export default function AdminAvailability() {
  const { logout, authHeaders } = useAuth();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [blockDate, setBlockDate] = useState("");

  useEffect(() => {
    axios.get(`${API}/admin/settings`, { headers: authHeaders() })
      .then((r) => setS(r.data))
      .catch((err) => { if (err.response?.status === 401) logout(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (patch) => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/admin/settings`, patch, { headers: authHeaders() });
      setS(data);
      toast.success("Saved.");
    } catch { toast.error("Save failed."); } finally { setSaving(false); }
  };

  if (!s) return <p className="text-zinc-500 font-mono text-xs">Loading...</p>;
  const av = s.availability;

  const setHours = (day, k, v) =>
    setS((prev) => ({ ...prev, business_hours: { ...prev.business_hours, [day]: { ...prev.business_hours[day], [k]: v } } }));

  const setAv = (k, v) => setS((prev) => ({ ...prev, availability: { ...prev.availability, [k]: v } }));

  const toggleWorkDay = (d) => {
    const days = av.work_days.includes(d) ? av.work_days.filter((x) => x !== d) : [...av.work_days, d];
    setAv("work_days", days);
  };

  return (
    <div className="space-y-8" data-testid="availability-panel">
      {/* accepting switch */}
      <div className="rounded-xl bg-[#121216] border border-[#d4af37]/30 p-5 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h3 className="font-display text-lg uppercase text-white">Accepting Online Bookings</h3>
          <p className="text-[10px] font-mono text-zinc-500 mt-1">When off, customers can still browse services and request quotes — they just can't book appointments.</p>
        </div>
        <button type="button"
          onClick={() => save({ availability: { ...av, accepting_bookings: !av.accepting_bookings } })}
          data-testid="accepting-bookings-switch"
          className={`px-6 py-3 rounded font-bold text-xs font-mono uppercase tracking-wider transition-colors ${
            av.accepting_bookings ? "bg-emerald-500/20 border border-emerald-500 text-emerald-400" : "bg-red-500/15 border border-red-500/50 text-red-400"}`}>
          {av.accepting_bookings ? "ON" : "OFF"}
        </button>
      </div>

      {/* business hours */}
      <div className="rounded-xl bg-[#121216] border border-zinc-800 p-5">
        <h3 className="font-display text-lg uppercase text-white mb-1">Business Hours</h3>
        <p className="text-[10px] font-mono text-zinc-500 mb-4">Shown on the public website. Leave times empty to hide.</p>
        <div className="space-y-2">
          {DAYS.map((d) => {
            const h = s.business_hours[d] || { open: "", close: "", closed: true };
            return (
              <div key={d} className="flex flex-wrap items-center gap-3" data-testid={`hours-${d}`}>
                <span className="w-24 text-xs font-mono uppercase text-zinc-300">{d.slice(0, 3)}</span>
                <label className="flex items-center gap-1.5 text-[10px] font-mono text-zinc-400">
                  <input type="checkbox" checked={!h.closed} onChange={(e) => setHours(d, "closed", !e.target.checked)}
                    data-testid={`hours-open-${d}`} className="accent-[#d4af37]" /> Open
                </label>
                {!h.closed && (
                  <>
                    <input type="time" value={h.open} onChange={(e) => setHours(d, "open", e.target.value)}
                      data-testid={`hours-from-${d}`} className={inputCls} />
                    <span className="text-zinc-600 text-xs">to</span>
                    <input type="time" value={h.close} onChange={(e) => setHours(d, "close", e.target.value)}
                      data-testid={`hours-to-${d}`} className={inputCls} />
                  </>
                )}
                {h.closed && <span className="text-[10px] font-mono text-zinc-600">Closed</span>}
              </div>
            );
          })}
        </div>
        <button type="button" onClick={() => save({ business_hours: s.business_hours })} disabled={saving}
          data-testid="save-hours" className="mt-4 px-5 py-2 rounded bg-[#d4af37] text-black text-[10px] font-mono font-bold uppercase tracking-wider hover:bg-[#e6ca65] transition-colors disabled:opacity-50">
          Save Hours
        </button>
      </div>

      {/* appointment availability */}
      <div className="rounded-xl bg-[#121216] border border-zinc-800 p-5">
        <h3 className="font-display text-lg uppercase text-white mb-4">Appointment Availability</h3>
        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          <div><span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1">Appointment duration (min)</span>
            <input type="number" value={av.slot_minutes} onChange={(e) => setAv("slot_minutes", Number(e.target.value))} data-testid="av-duration" className={inputCls} /></div>
          <div><span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1">Travel buffer (min)</span>
            <input type="number" value={av.buffer_minutes} onChange={(e) => setAv("buffer_minutes", Number(e.target.value))} data-testid="av-buffer" className={inputCls} /></div>
          <div><span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1">First slot</span>
            <input type="time" value={av.day_start} onChange={(e) => setAv("day_start", e.target.value)} data-testid="av-start" className={inputCls} /></div>
          <div><span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1">Last slot starts by</span>
            <input type="time" value={av.day_end} onChange={(e) => setAv("day_end", e.target.value)} data-testid="av-end" className={inputCls} /></div>
          <div><span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1">Max bookings per day</span>
            <input type="number" value={av.max_daily} onChange={(e) => setAv("max_daily", Number(e.target.value))} data-testid="av-max-daily" className={inputCls} /></div>
          <div><span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1">Minimum notice (hours)</span>
            <input type="number" value={av.min_notice_hours} onChange={(e) => setAv("min_notice_hours", Number(e.target.value))} data-testid="av-notice" className={inputCls} /></div>
        </div>
        <div className="flex flex-wrap gap-2 mb-4">
          {DAYS.map((d) => (
            <button key={d} type="button" onClick={() => toggleWorkDay(d)} data-testid={`av-day-${d}`}
              className={`px-3 py-1.5 rounded border text-[10px] font-mono uppercase transition-colors ${
                av.work_days.includes(d) ? "bg-[#d4af37]/15 border-[#d4af37] text-[#d4af37]" : "border-zinc-700 text-zinc-500"}`}>
              {d.slice(0, 3)}
            </button>
          ))}
          <label className="flex items-center gap-1.5 text-[10px] font-mono text-zinc-400 ml-3">
            <input type="checkbox" checked={av.same_day} onChange={(e) => setAv("same_day", e.target.checked)}
              data-testid="av-same-day" className="accent-[#d4af37]" /> Same-day bookings
          </label>
        </div>
        <button type="button" onClick={() => save({ availability: s.availability })} disabled={saving}
          data-testid="save-availability" className="px-5 py-2 rounded bg-[#d4af37] text-black text-[10px] font-mono font-bold uppercase tracking-wider hover:bg-[#e6ca65] transition-colors disabled:opacity-50">
          Save Availability
        </button>
      </div>

      {/* blocked dates */}
      <div className="rounded-xl bg-[#121216] border border-zinc-800 p-5">
        <h3 className="font-display text-lg uppercase text-white mb-1">Blocked Dates &amp; Vacation</h3>
        <p className="text-[10px] font-mono text-zinc-500 mb-4">No appointments can be booked on these dates.</p>
        <div className="flex flex-wrap gap-2 mb-3">
          {(s.blocked_dates || []).map((d) => (
            <span key={d} className="px-3 py-1.5 rounded bg-red-500/10 border border-red-500/30 text-red-400 text-[10px] font-mono flex items-center gap-2">
              {d}
              <button type="button" onClick={() => save({ blocked_dates: s.blocked_dates.filter((x) => x !== d) })}
                data-testid={`unblock-${d}`} className="hover:text-white">×</button>
            </span>
          ))}
          {(!s.blocked_dates || s.blocked_dates.length === 0) && <span className="text-[10px] font-mono text-zinc-600">None</span>}
        </div>
        <div className="flex gap-2">
          <input type="date" value={blockDate} onChange={(e) => setBlockDate(e.target.value)} data-testid="block-date-input" className={inputCls} />
          <button type="button" disabled={!blockDate}
            onClick={() => { save({ blocked_dates: [...(s.blocked_dates || []), blockDate] }); setBlockDate(""); }}
            data-testid="block-date-btn"
            className="px-4 py-2 rounded border border-[#d4af37]/40 text-[10px] font-mono uppercase tracking-wider text-[#d4af37] hover:bg-[#d4af37] hover:text-black transition-colors disabled:opacity-40">
            Block Date
          </button>
        </div>
      </div>
    </div>
  );
}
